// src/modules/forge/store/index.ts

import {
  getForgeToken,
  listBuckets,
  listObjects,
  translateModel,
  checkTranslationStatus,
  uploadFileViaS3,
  fetchDbProperties,
  updateDbProperties,
  fetchModuleSelections,
  saveModuleSelections,
} from "../api";
import { dispatchNotification } from "@/modules/common/utils/notifications";

export default {
  namespaced: true,
  state: {
    token: null,
    buckets: [],
    objects: [],
    selectedBucket: null,
    selectedObject: null,
    translationStatuses: {} as Record<string, string>,
    // Новый флаг для лоадера:
    isLoading: false,
    dbProperties: {},

    currentDoc: null,
    sheets: [],
    selectedSheet: null,

    moduleSelections: [], // тут будем хранить [{ element_db_id, module_number, work_type, checked }, ...]
  },

  getters: {
    GET_TOKEN: (state: any) => state.token,
    GET_BUCKETS: (state: any) => state.buckets,
    GET_OBJECTS: (state: any) => state.objects,
    GET_SELECTED_BUCKET: (state: any) => state.selectedBucket,
    GET_SELECTED_OBJECT: (state: any) => state.selectedObject,
    GET_TRANSLATION_STATUS: (state: any) => (key: string) => {
      return state.translationStatuses[key] ?? "";
    },
    GET_LOADING: (state: any) => state.isLoading,
    GET_DB_PROPERTIES: (state: any) => state.dbProperties,

    GET_SHEETS: (state: any) => state.sheets,
    GET_SELECTED_SHEET: (state: any) => state.selectedSheet,
    GET_CURRENT_DOC: (state: any) => state.currentDoc,

    GET_MODULE_SELECTIONS: (state: any) => state.moduleSelections,
  },

  mutations: {
    SET_TOKEN(state: any, token: string) {
      state.token = token;
    },
    SET_BUCKETS(state: any, buckets: Array<any>) {
      state.buckets = buckets;
    },
    SET_OBJECTS(state: any, objects: Array<any>) {
      state.objects = objects;
    },
    SET_SELECTED_BUCKET(state: any, bucketKey: string) {
      state.selectedBucket = bucketKey;
    },
    SET_SELECTED_OBJECT(state: any, object: any) {
      state.selectedObject = object;
    },
    SET_TRANSLATION_STATUS(state: any, payload: { key: string; status: string }) {
      state.translationStatuses[payload.key] = payload.status;
    },
    SET_LOADING(state: any, flag: boolean) {
      state.isLoading = flag;
    },
    SET_DB_PROPERTIES(state: any, payload: any) {
      state.dbProperties = payload;
    },

    SET_DOC(state: any, doc: any) {
      state.currentDoc = doc;
    },
    SET_SHEETS(state: any, sheets: any[]) {
      state.sheets = sheets;
    },
    SET_SELECTED_SHEET(state: any, sheet: any) {
      state.selectedSheet = sheet;
    },

    SET_MODULE_SELECTIONS(state: any, items: Array<any>) {
      state.moduleSelections = items;
    },
  },

  actions: {
    async FETCH_TOKEN(context: any) {
      context.commit("SET_LOADING", true);
      try {
        const data = await getForgeToken();
        context.commit("SET_TOKEN", data.access_token);
      } catch (error: any) {
        console.error(`Error FETCH_TOKEN => ${error}`);
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async FETCH_BUCKETS(context: any) {
      context.commit("SET_LOADING", true);
      try {
        const data = await listBuckets();
        context.commit("SET_BUCKETS", data.items);
      } catch (error: any) {
        console.error(`Error FETCH_BUCKETS => ${error}`);
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async FETCH_OBJECTS(context: any, bucketKey: string) {
      context.commit("SET_LOADING", true);
      try {
        const data = await listObjects(bucketKey);
        context.commit("SET_OBJECTS", data.items);
      } catch (error: any) {
        console.error(`Error FETCH_OBJECTS => ${error}`);
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async SELECT_BUCKET(context: any, bucketKey: string) {
      context.commit("SET_LOADING", true);
      try {
        context.commit("SET_SELECTED_BUCKET", bucketKey);
        await context.dispatch("FETCH_OBJECTS", bucketKey);
      } catch (error: any) {
        console.error(`Error SELECT_BUCKET => ${error}`);
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async SELECT_OBJECT(context: any, object: any) {
      context.commit("SET_LOADING", true);
      try {
        context.commit("SET_SELECTED_OBJECT", object);
        //await context.dispatch("FETCH_METADATA", object.dbId);
        //await context.dispatch("TRANSLATE_MODEL", object.objectKey);
      } catch (error: any) {
        console.error(`Error SELECT_OBJECT => ${error}`);
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async TRANSLATE_MODEL(context: any, objectKey: string) {
      context.commit("SET_LOADING", true);
      try {
        const urn = `urn:adsk.objects:os.object:test-bucket-2025/${objectKey}`;
        await translateModel(urn);
      } catch (error: any) {
        console.error(`Error TRANSLATE_MODEL => ${error}`);
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async START_TRANSLATION(context: any, objectKey: string) {
      context.commit("SET_LOADING", true);
      try {
        const urn = `urn:adsk.objects:os.object:test-bucket-2025/${objectKey}`;
        await translateModel(urn);
        dispatchNotification("info", "Выполнено", "Запущен перевод модели...");

        context.commit("SET_TRANSLATION_STATUS", { key: objectKey, status: "inprogress" });

        // Как только отправили запрос, можно снять лоадер
        context.commit("SET_LOADING", false);

        // Запускаем поллинг статуса
        context.dispatch("POLL_TRANSLATION_STATUS", { objectKey, urn });
      } catch (error: any) {
        console.error(`Error START_TRANSLATION => ${error}`);
        dispatchNotification("error", "Ошибка", "Не удалось запустить перевод модели.");
        context.commit("SET_LOADING", false);
      }
    },

    POLL_TRANSLATION_STATUS(context: any, payload: { objectKey: string; urn: string }) {
      let attempts = 0;
      const maxAttempts = 10;

      const intervalId = setInterval(async () => {
        attempts++;

        try {
          const manifestData = await checkTranslationStatus(payload.urn);
          const status = manifestData.status; // "success" | "inprogress" | "failed" | ...
          context.commit("SET_TRANSLATION_STATUS", { key: payload.objectKey, status });

          if (status === "success" || status === "failed") {
            clearInterval(intervalId);
            if (status === "success") {
              dispatchNotification("success", "Готово", "Перевод успешно завершён!");
            } else {
              dispatchNotification("error", "Ошибка", "Перевод не удался!");
            }
          }
        } catch (err: any) {
          console.error("Ошибка при проверке статуса:", err);
          clearInterval(intervalId);
          context.commit("SET_TRANSLATION_STATUS", { key: payload.objectKey, status: "failed" });
          dispatchNotification(
            "error",
            "Ошибка",
            "Произошла ошибка при проверке статуса перевода.",
          );
        }

        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
          context.commit("SET_TRANSLATION_STATUS", { key: payload.objectKey, status: "timeout" });
          dispatchNotification(
            "info",
            "Тайм-аут",
            "Превышено время ожидания перевода (10 секунд). Статус не ясен.",
          );
        }
      }, 1000);
    },

    async UPLOAD_FILE(context: any, payload: { bucketName: string; file: File }) {
      context.commit("SET_LOADING", true);
      try {
        const data = await uploadFileViaS3(payload.bucketName, payload.file);
        dispatchNotification(
          "success",
          "Успех",
          `Файл ${data.objectKey} загружен в ${data.bucketName}.`,
        );
        // После удачной загрузки — перезагрузим список объектов
        await context.dispatch("FETCH_OBJECTS", payload.bucketName);
      } catch (error: any) {
        console.error("UPLOAD_FILE error =>", error);
        dispatchNotification(
          "error",
          "Ошибка загрузки",
          error.message || "Невозможно загрузить файл.",
        );
      } finally {
        context.commit("SET_LOADING", false);
      }
    },

    async FETCH_DB_PROPERTIES(
      context: any,
      { forgeModelId, forgeElementId }: { forgeModelId: string; forgeElementId: number },
    ) {
      try {
        const data = await fetchDbProperties(forgeModelId, forgeElementId);
        context.commit("SET_DB_PROPERTIES", data.properties || {});
      } catch (error) {
        console.error("Error FETCH_DB_PROPERTIES =>", error);
        context.commit("SET_DB_PROPERTIES", {});
      }
    },

    async UPDATE_DB_PROPERTIES(
      context: any,
      {
        forgeModelId,
        forgeElementId,
        properties,
      }: { forgeModelId: string; forgeElementId: number; properties: Record<string, any> },
    ) {
      try {
        const data = await updateDbProperties(forgeModelId, forgeElementId, properties);
        // data.properties вернётся из PUT /properties
        context.commit("SET_DB_PROPERTIES", data.properties || {});
      } catch (error) {
        console.error("Error UPDATE_DB_PROPERTIES =>", error);
      }
    },

    SELECT_SHEET(context: any, sheet: any) {
      context.commit("SET_SELECTED_SHEET", sheet);
    },

    async FETCH_MODULE_SELECTIONS(context: any, forgeModelId: string) {
      try {
        // вызывает fetchModuleSelections(...) из src/modules/forge/api/index.ts
        const data = await fetchModuleSelections(forgeModelId);
        // data = { items: [...] }
        context.commit("SET_MODULE_SELECTIONS", data.items || []);
      } catch (err) {
        console.error("FETCH_MODULE_SELECTIONS error =>", err);
        context.commit("SET_MODULE_SELECTIONS", []);
      }
    },

    // Добавим экшен SAVE_MODULE_SELECTIONS
    async SAVE_MODULE_SELECTIONS(
      context: any,
      payload: {
        forge_model_id: string;
        changes: Array<{
          element_db_id: number;
          module_number: string;
          work_type: string;
          checked: boolean;
        }>;
      },
    ) {
      try {
        await saveModuleSelections(payload);
        // После сохранения можно снова обновить список
        // (чтобы гарантированно подтянуть актуальные данные с сервера)
        await context.dispatch("FETCH_MODULE_SELECTIONS", payload.forge_model_id);
      } catch (err) {
        console.error("SAVE_MODULE_SELECTIONS error =>", err);
      }
    },
  },
};
